/**
 * DevConsole section for poking the swappable token + milestone stubs.
 *
 * These controls exist so the in-game UI can be demoed before the real
 * pump.fun feed and on-chain latch subscription land:
 *   - Override the mcap to any value (or release the override).
 *   - Bump the mcap by a multiplier in one tap.
 *   - Fire a fake milestone-crossing event into `milestoneEvents`, which
 *     fans out to the toast + the in-panel log row.
 *
 * When the real sources replace the stubs, this section can stay as a
 * fallback (real impls are expected to no-op on `setManual` / `emitStub`)
 * or be deleted — nothing else depends on it.
 */
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import { useEffect, useState } from "react";

import { mcapSource } from "../../token/mcapSource";
import { milestoneEvents, useMilestoneLog } from "../../token/milestoneEvents";
import { milestoneTargets } from "../../token/milestoneTargets";
import { btnStyle, colors, fmtSol, shortKey, styles } from "./_shared";

export function TokenStubsSection(): JSX.Element {
  const [mcapLamports, setMcapLamports] = useState(() => mcapSource.sampleNow().lamports);
  const [manualInput, setManualInput] = useState("");
  useEffect(() => mcapSource.subscribe((s) => setMcapLamports(s.lamports)), []);

  const targets = milestoneTargets.sample(mcapLamports);
  const entries = useMilestoneLog((s) => s.entries);
  const clearLog = useMilestoneLog((s) => s.clear);

  const setOverrideFromInput = (): void => {
    const sol = Number(manualInput);
    if (!Number.isFinite(sol) || sol <= 0) return;
    mcapSource.setManual(Math.floor(sol * LAMPORTS_PER_SOL));
  };

  const bump = (factor: number): void => {
    mcapSource.setManual(Math.floor(mcapLamports * factor));
  };

  const fireLatch = (): void => {
    const payoutLamports = Math.floor(0.05 * LAMPORTS_PER_SOL * 8);
    milestoneEvents.emitStub({
      postedMcapLamports: targets.nextThresholdLamports,
      thresholdLamports: targets.nextThresholdLamports,
      payoutLamports,
      winners: ["StubWinnerAA…aa11", "StubWinnerBB…bb22", "StubWinnerCC…cc33"],
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.sectionHeader}>
        <div style={styles.sectionTitle}>Token stubs</div>
        <div style={styles.sectionSubtitle}>swap when real feed lands</div>
      </div>

      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>Mcap source</div>
          <div style={{ ...styles.mono, fontSize: 13 }}>
            {fmtSol(mcapLamports)} SOL
          </div>
          <div style={{ ...styles.label, color: colors.textFaint }}>
            peak {fmtSol(targets.peakLamports)} · next {fmtSol(targets.nextThresholdLamports)}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <button onClick={() => bump(1.1)} style={btnStyle({ small: true })}>+10%</button>
            <button onClick={() => bump(1.5)} style={btnStyle({ small: true })}>×1.5</button>
            <button onClick={() => bump(2)} style={btnStyle({ small: true })}>×2</button>
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input
              type="number"
              value={manualInput}
              placeholder="set mcap (SOL)"
              onChange={(e) => setManualInput(e.target.value)}
              style={styles.inputStyle}
            />
            <button onClick={setOverrideFromInput} style={btnStyle({ tone: "primary", small: true })}>
              set
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>Milestone latch</div>
          <div style={{ fontSize: 11, color: colors.textMute }}>
            Fires a fake crossing event. Fans out to MilestoneToast and the
            log row inside TokenMcapPanel.
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <button onClick={fireLatch} style={btnStyle({ tone: "primary" })}>
              Fire latch crossing
            </button>
            <button onClick={clearLog} style={btnStyle({ tone: "ghost", small: true })}>
              Clear log
            </button>
          </div>
        </div>

        <div style={styles.card}>
          <div style={{ ...styles.label, color: colors.textMute }}>
            Log ({entries.length})
          </div>
          {entries.length === 0 ? (
            <div style={{ ...styles.mono, color: colors.textMute }}>(empty)</div>
          ) : (
            entries.map((e) => (
              <div key={e.id} style={{ ...styles.mono, fontSize: 10 }}>
                #{e.id} · {fmtSol(e.thresholdLamports)} SOL → {fmtSol(e.payoutLamports)} paid · {e.winners.length} winners
                {e.winners[0] ? ` · ${shortKey(e.winners[0])}…` : ""}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
